import React, { useState, useEffect, useCallback } from "react";
import {
  Stepper,
  Step,
  StepLabel,
  Typography,
  makeStyles,
  Box,
  Paper,
  Button,
  Snackbar,
  IconButton,
  CircularProgress,
} from "@material-ui/core";
import MuiAlert from "@material-ui/lab/Alert";
import CloseIcon from "@material-ui/icons/Close";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import CMODetails from "./steps/CMODetails";
import CMOReview from "./steps/CMOReview";
import { useHistory, useLocation } from "react-router-dom";
import SiteDetails from "./steps/SiteDetails";
import PlantDetails from "./steps/PlantDetails";
import ShipmentConfigurations from "./steps/Configurations";
import PurchasingDetails from "./steps/PurchasingDetails";
import OptionsPermissions from "./steps/OptionsPermissions";
import useIsMounted from "../../../../hooks/useIsMounted";
import "../CMOConfigDetails.css";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
  },
  stepper: {
    maxWidth: "90%",
    margin: "0 auto",
    padding: "50px 24px",
  },
  stepCompleted: {
    color: "#4CAF50",
  },
  backButton: {
    display: "flex",
    alignItems: "center",
    color: "#ec6535",
    marginBottom: "1rem",
    marginTop: "1rem",
    cursor: "pointer",
    "&:hover": {
      textDecoration: "underline",
    },
  },
  stepperHeader: {
    padding: "32px 0",
    textAlign: "center",
    backgroundColor: "#F5F9FF",
    position: "relative",
  },
  heading: {
    fontWeight: 500,
    fontSize: 30,
    lineHeight: "100%",
    marginBottom: 20,
  },
  subheading: {
    fontWeight: 400,
    fontSize: 16,
    lineHeight: "100%",
  },
  stepLabel: {
    flexDirection: "column",
    fontSize: 15,
    position: "relative",
    "& .MuiStepLabel-iconContainer": {
      padding: 0,
    },
    "& .MuiStepLabel-labelContainer": {
      width: "auto",
      minWidth: 140,
      position: "absolute",
      left: "50%",
      top: 30,
      transform: " translateX(-50%)",
      textAlign: "center",
    },
  },
}));

const steps = [
  "CMO Details",
  "Site Details",
  "Manufacturing Plant Details",
  "Configurations",
  "Purchasing Details",
  "Options & Permissions",
  "Review And Add CMO",
];

const CMOStepper = () => {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const isMounted = useIsMounted();
  const [activeStep, setActiveStep] = useState(0);
  const [navigatedBackToPlantDetails, setNavigatedBackToPlantDetails] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  // Use formData with a key for each step
  const [formData, setFormData] = useState({
    cmoDetails: {},
    vendorPlantDetails: {},
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [activeStep]);

  // Load persisted form data and active step when component mounts
  useEffect(() => {
    try {
      // Restore current step
      const savedStep = sessionStorage.getItem("cmoCurrentStep");
      if (savedStep !== null) {
        const stepIndex = parseInt(savedStep, 10);
        if (!isNaN(stepIndex) && stepIndex >= 0 && stepIndex < steps.length) {
          setActiveStep(stepIndex);
        }
      }

      // Restore form data
      const savedFormData = sessionStorage.getItem("cmoConfigFormData");
      if (savedFormData) {
        setFormData(JSON.parse(savedFormData));
      }

      // Check if we have data from previous screen
      const selectedCMO = location.state?.selectedCMO;
      const selectedCMOFormData = location.state?.selectedCMOFormData;

      if (selectedCMOFormData) {
        // Transform the data structure to match the required format
        const transformedData = transformCMOFormData(selectedCMOFormData);

        // Set form data from the transformed response
        setFormData(transformedData);

        // Also persist this in session storage
        sessionStorage.setItem(
          "cmoConfigFormData",
          JSON.stringify(transformedData)
        );
      } else if (selectedCMO) {
        // Initialize with some basic data from the selected CMO
        const initialData = {
          ...formData,
          cmoDetails: {
            ...formData.cmoDetails,
            cmoName: selectedCMO.cmoName,
            cmoId: selectedCMO.cmoId,
            // Add other fields as needed
          },
        };
        setFormData(initialData);
        sessionStorage.setItem(
          "cmoConfigFormData",
          JSON.stringify(initialData)
        );
      }

      setIsInitialized(true);
    } catch (error) {
      console.error("Error loading persisted form data:", error);
      setSnackbar({
        open: true,
        message: "Failed to load saved data",
        severity: "error",
      });
      setIsInitialized(true);
    }
  }, [location.state]);

  // Memoize updateStepData to prevent unnecessary re-renders
  const updateStepData = useCallback(
    (stepKey, stepData) => {
      try {
        if (isMounted()) {
          setFormData((prev) => {
            const updatedData = { ...prev, [stepKey]: stepData };

            // Persist to session storage
            try {
              sessionStorage.setItem(
                "cmoConfigFormData",
                JSON.stringify(updatedData)
              );
            } catch (error) {
              console.error("Error saving to sessionStorage:", error);
            }

            return updatedData;
          });
        }
      } catch (error) {
        console.error(`Error updating step data for ${stepKey}:`, error);
      }
    },
    [isMounted]
  );
  const handleNext = (stepIndex) => {
    try {
      let nextStep;
      if (typeof stepIndex === "number") {
        nextStep = stepIndex;
      } else {
        nextStep = activeStep + 1;
      }

      // Save current step completion status
      const completedSteps = JSON.parse(
        sessionStorage.getItem("cmoCompletedSteps") || "[]"
      );
      if (!completedSteps.includes(activeStep)) {
        completedSteps.push(activeStep);
        sessionStorage.setItem(
          "cmoCompletedSteps",
          JSON.stringify(completedSteps)
        );
      }

      // Save current step to sessionStorage
      sessionStorage.setItem("cmoCurrentStep", nextStep);

      // Update the active step
      if (isMounted()) {
        setActiveStep(nextStep);
      }
    } catch (error) {
      console.error("Error in handleNext:", error);
      if (isMounted()) {
        setSnackbar({
          open: true,
          message: "An error occurred while proceeding to next step",
          severity: "error",
        });
      }
    }
  };

  const handleBack = () => {
    try {
      if (activeStep === undefined || activeStep <= 0) {
        throw new Error("Already at first step");
      }

      // Step 2 is Manufacturing Plant Details - set flag when navigating back to it
      const nextStep = Math.max(0, activeStep - 1);
      if (nextStep === 2) {
        setNavigatedBackToPlantDetails(true);
      }

      setActiveStep((prevActiveStep) => {
        const newStep = Math.max(0, prevActiveStep - 1);
        // Save current step to sessionStorage
        sessionStorage.setItem("cmoCurrentStep", newStep);
        return newStep;
      });
    } catch (error) {
      console.error("Error in handleBack:", error);
      setSnackbar({
        open: true,
        message: "Cannot go back from first step",
        severity: "error",
      });
    }
  };

  const handleSubmit = async () => {
    try {
      setIsLoading(true);
      // Add your form submission logic here
      setSnackbar({
        open: true,
        message: "CMO configuration submitted successfully",
        severity: "success",
      });

      // Clear session storage after successful submission
      setTimeout(() => {
        sessionStorage.removeItem("cmoConfigFormData");
        sessionStorage.removeItem("cmoCurrentStep");
        sessionStorage.removeItem("cmoCompletedSteps");
        history.goBack();
      }, 2000);
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || "An error occurred during submission",
        severity: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbar((prev) => ({ ...prev, open: false }));
  };
  const handleGoBack = () => {
    // Clear session storage when navigating away from the form
    sessionStorage.removeItem("cmoConfigFormData");
    sessionStorage.removeItem("cmoCurrentStep");
    sessionStorage.removeItem("cmoCompletedSteps");

    // Reset snackbar state after going back
    setSnackbar({ open: false, message: "", severity: "success" });

    history.goBack();
  };
  // Function to mark API call as successful and handle navigation
  const markApiCallSuccessful = useCallback(
    (shouldNavigate = true, nextStepIndex, stepName = null) => {
      // Save current step completion status
      const completedSteps = JSON.parse(
        sessionStorage.getItem("cmoCompletedSteps") || "[]"
      );
      if (!completedSteps.includes(activeStep)) {
        completedSteps.push(activeStep);
        sessionStorage.setItem(
          "cmoCompletedSteps",
          JSON.stringify(completedSteps)
        );
      }

      // Determine which step name to use in the message
      const displayStepName = stepName || steps[activeStep];

      // Set snackbar first
      setSnackbar({
        open: true,
        message: `${displayStepName} saved successfully`,
        severity: "success",
      });

      // Navigate if needed
      if (shouldNavigate) {
        const nextStep =
          typeof nextStepIndex === "number" ? nextStepIndex : activeStep + 1;
        sessionStorage.setItem("cmoCurrentStep", nextStep);
        setActiveStep(nextStep);
      }
    },
    [activeStep, steps]
  );

  const getStepContent = (step) => {
    if (step === undefined || step === null) {
      console.error("Invalid step value");
      return null;
    }

    // Create common props with error handling
    const commonProps = {
      formData: formData || {},
      updateStepData,
      classes: classes || {},
      handleNext,
      handleBack,
      handleSubmit,
      activeStep,
      stepsLength: steps.length,
      selectedCMOFormData: location.state?.selectedCMOFormData,
      markApiCallSuccessful, // Pass the function to child components
    };

    try {
      switch (step) {
        case 0:
          return (
            <CMODetails
              setFormData={setFormData}
              {...commonProps}
              formData={formData.cmoDetails}
              updateStepData={(data) => updateStepData("cmoDetails", data)}
              selectedCMOFormData={location.state?.selectedCMOFormData}
              originalCmoOrgDetails={location.state?.cmoOrgDetails}
            />
          );
        case 1:
          return (
            <SiteDetails
              {...commonProps}
              cmoStepDetails={formData.cmoDetails}
              formData={formData.vendorPlantDetails}
              updateStepData={(data) =>
                updateStepData("vendorPlantDetails", data)
              }
              selectedCMOFormData={location.state?.selectedCMOFormData}
              originalVendorPlantDetails={location.state?.cmoVendorDetails}
            />
          );
        case 2:
          return (
            <PlantDetails
              {...commonProps}
              formData={formData.vendorPlantDetails}
              updateStepData={(data) =>
                updateStepData("vendorPlantDetails", data)
              }
              selectedCMOFormData={location.state?.selectedCMOFormData}
              originalVendorPlantDetails={location.state?.cmoVendorDetails}
              navigatedBackToPlantDetails={navigatedBackToPlantDetails}
              onNavigatedBackHandled={() => setNavigatedBackToPlantDetails(false)}
            />
          );
        case 3:
          return (
            <ShipmentConfigurations
              {...commonProps}
              formData={formData.vendorPlantDetails}
              updateStepData={(data) =>
                updateStepData("vendorPlantDetails", data)
              }
              selectedCMOFormData={location.state?.selectedCMOFormData}
              originalVendorPlantDetails={location.state?.cmoVendorDetails}
            />
          );
        case 4:
          return (
            <PurchasingDetails
              {...commonProps}
              formData={formData.vendorPlantDetails}
              updateStepData={(data) =>
                updateStepData("vendorPlantDetails", data)
              }
              selectedCMOFormData={location.state?.selectedCMOFormData}
              originalVendorPlantDetails={location.state?.cmoVendorDetails}
            />
          );
        case 5:
          return (
            <OptionsPermissions
              {...commonProps}
              formData={formData.vendorPlantDetails}
              updateStepData={(data) =>
                updateStepData("vendorPlantDetails", data)
              }
              selectedCMOFormData={location.state?.selectedCMOFormData}
              originalVendorPlantDetails={location.state?.cmoVendorDetails}
            />
          );
        case 6:
          return <CMOReview {...commonProps} formData={formData} />;
        default:
          return <Typography color="error">Unknown step</Typography>;
      }
    } catch (error) {
      console.error("Error rendering step content:", error);
      return (
        <div>
          <Typography color="error">
            An error occurred while loading this step. Please try again.
          </Typography>
          <Button
            onClick={() => window.location.reload()}
            variant="outlined"
            color="primary"
          >
            Reload
          </Button>
        </div>
      );
    }
  };
  // Reset snackbar on mount
  useEffect(() => {
    setSnackbar({ open: false, message: "", severity: "success" });
  }, []);

  // Function to transform the API response data structure to match the required format
  const transformCMOFormData = (data) => {
    // Create the new structure
    const transformedData = {
      cmoDetails: {
        status: data.cmoDetails?.status || "Active",
        organizationDetails: {
          organizationName: data.cmoDetails?.cmoName || "",
          orgShortName: data.cmoDetails?.cmoShortFormName || "",
          streetAddress1: data.cmoDetails?.streetAddress1 || "",
          streetAddress2: data.cmoDetails?.streetAddress2 || "",
          city: data.cmoDetails?.city || "",
          state: data.cmoDetails?.state || "",
          country: data.cmoDetails?.country || "",
          postCode: data.cmoDetails?.postCode || "",
        },
        adminDetails: {
          fullName: data.cmoDetails?.fullName || "",
          phone: data.cmoDetails?.phoneNumber || "",
          email: data.cmoDetails?.emailAddress || "",
          collaboratorRoleId: 2,
          collaboratorRoleName: "Admin",
          collaboratorTypeId: 2,
        },
      },
      vendorPlantDetails: {
        ...data.vendorPlantDetails,
        twoStep: false,
        cannotEditPhone: false,
        azureAd: false,
        countryName: data.vendorPlantDetails?.country || "",
        // country: getCountryCode(data.vendorPlantDetails?.country),
        stateName: data.vendorPlantDetails?.state || "",
        // state: getStateCode(data.vendorPlantDetails?.state),
        drugTypeIDList:
          data.vendorPlantDetails?.drugTypeIDs?.map(
            (item) => item.drugTypeID
          ) || [],
        plantTypeIDList:
          data.vendorPlantDetails?.plantTypeIDs?.map(
            (item) => item.plantTypeID
          ) || [],
        facilityTypeIDList:
          data.vendorPlantDetails?.facilityTypeIDs?.map(
            (item) => item.facilityTypeID
          ) || [],
        supplyTypeIDList:
          data.vendorPlantDetails?.supplyTypeIDs?.map(
            (item) => item.supplyTypeID
          ) || [],
        vendorProductTypeConfigs:
          data.vendorPlantDetails?.vendorProductTypeConfigs || [],
        documentTypesList:
          data.vendorPlantDetails?.cmoDocumentTypesList?.map((item) => ({
            documentTypeID: item.documentTypeID,
            documentTypeName: item.documentTypeName,
            activated: item.activated,
            newDocumentTypeName: item.newDocumentTypeName || "NA",
            documentType: "Default",
            documentCategoryID: item.documentCategoryID,
            documentCategoryName: item.documentCategoryName || "",
            mandatoryInProcessWorkflow: item.mandatoryInProcessWorkflow || false,
          })) || [],
        allowMultipleBatches: data.vendorPlantDetails?.cmoBatchShipmentSettingID
          ? [
              "Yes, for batches with the same brand",
              "No, single batch only",
              "Yes, for batches with same and different brands",
            ][data.vendorPlantDetails.cmoBatchShipmentSettingID - 1] ||
            "Yes, for batches with the same brand"
          : "Yes, for batches with the same brand",
        shipmentDuration:
          data.vendorPlantDetails?.shipmentDuration?.toString() || "",
        automateGoodsReceiptProcess:
          data.vendorPlantDetails?.automateGRP === true
            ? "Yes"
            : data.vendorPlantDetails?.automateGRP === false
            ? "No"
            : "",
        cmoUsePOInterface:
          data.vendorPlantDetails?.cmoToUsePOInterface === true
            ? "Yes"
            : data.vendorPlantDetails?.cmoToUsePOInterface === false
            ? "No"
            : "",
        acceptanceLimitEarlyDelivery:
          data.vendorPlantDetails?.acceptanceLimitForEarlyDeliveryDateChangeByCMO?.toString() ||
          "",
        acceptanceLimitLaterDelivery:
          data.vendorPlantDetails?.acceptanceLimitForLaterDeliveryDateChangeByCMO?.toString() ||
          "",
        acceptanceLimitLessQuantity:
          data.vendorPlantDetails?.acceptanceLimitForLessDeliveryQuantityChangeByCMO?.toString() ||
          "",
        acceptanceLimitMoreQuantity:
          data.vendorPlantDetails
            ?.acceptanceLimitForMoreDeliveryQuantityChangeByCMO?.toString() || "",
        batchAllocationPdfRequired:
          data.vendorPlantDetails?.batchAllocationPDFNotificationReqd === true
            ? "Yes"
            : data.vendorPlantDetails?.batchAllocationPDFNotificationReqd === false
            ? "No"
            : "Yes",
        sourceProductDoMEqualsMaterial:
          data.vendorPlantDetails?.domPresentOnDigitizationUI === true
            ? "Post Manufacturing Documents"
            : data.vendorPlantDetails?.domPresentOnDigitizationUI === false
            ? "Inventory"
            : "",
        pdfFieldTo: "CMO Name",
        pdfFieldCC: "All the users associated with the CMO, Brand and Product Type",
        pdfFieldSubject: data.vendorPlantDetails?.batchAllocationPDFNotificationSubject || "Batch Allocation done for Purchase Order 'PO Number' with Product 'BMS Product Code', Brand 'Brand Name'.",
        pdfFieldObjective: data.vendorPlantDetails?.batchAllocationPDFNotificationObjective || "The objective of this memo is to notify the 'CMO Name' of allocated (to be consumed) material/component batches for commercial manufacturing of Product 'BMS Product Code', Brand 'Brand name' based on Purchase Order 'PO Number' issued by 'Sponsor Name'.",
        siteNameFromReport: data.vendorPlantDetails?.siteNameFromReport || "",
        poDocType:
          data.vendorPlantDetails?.poDocumentTypesList?.map(
            (item) => item.poDocumentType
          ) || [],
        purchasingOrgList:
          data.vendorPlantDetails?.purchasingOrgList?.map((item) => ({
            code: item.purchasingOrgCode,
            description: item.purchasingOrgDescription,
          })) || [],
        purchasingGroupList:
          data.vendorPlantDetails?.purchasingGroupList?.map((item) => ({
            code: item.purchasingGroupCode,
            description: item.purchasingGroupDescription,
            telephoneNbr: item.purchasingGroupTelephone,
          })) || [],
      },
    };

    delete transformedData.vendorPlantDetails.cmoDocumentTypesList;

    return transformedData;
  };

  // // Helper function to get country code from country name
  // const getCountryCode = (countryName) => {
  //   // This is a simplified mapping, you might need a more comprehensive one
  //   const countryMap = {
  //     "United States": "US",
  //     "India": "IN",
  //     "United Kingdom": "GB",
  //     // Add more mappings as needed
  //   };
  //   return countryMap[countryName] || countryName;
  // };

  // // Helper function to get state code from state name
  // const getStateCode = (stateName) => {
  //   // This is a simplified mapping for US states, you might need a more comprehensive one
  //   const stateMap = {
  //     "Texas": "TX",
  //     "California": "CA",
  //     "New York": "NY",
  //     // Add more mappings as needed
  //   };
  //   return stateMap[stateName] || stateName;
  // };

  if (!isInitialized) {
    return (
      <Box p={3} display="flex" justifyContent="center">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <div className="config-cmo-stepper-wrapper">
      <Paper className={classes.root}>
        <Box className={classes.stepperHeader}>
          <Typography className={classes.heading} variant="h5">
            Add New CMO
          </Typography>
          <Typography className={classes.subheading}>
            Please add the CMO details and configurations in the fields below.
          </Typography>
          <Box className={`close-page-action`} onClick={handleGoBack}>
            <Typography>Cancel</Typography>
            <CloseIcon />
          </Box>
        </Box>
        <Stepper activeStep={activeStep} className={classes.stepper}>
          {steps.map((label, index) => {
            const stepProps = {};
            const labelProps = {};
            // Check if this step is completed from session storage
            const completedSteps = JSON.parse(
              sessionStorage.getItem("cmoCompletedSteps") || "[]"
            );
            const isStepCompleted =
              completedSteps.includes(index) || index < activeStep;

            return (
              <Step key={label} {...stepProps}>
                <StepLabel
                  className={classes.stepLabel}
                  {...labelProps}
                  StepIconProps={{
                    completed: isStepCompleted && (
                      <CheckCircleIcon className={classes.stepCompleted} />
                    ),
                  }}
                >
                  {label}
                </StepLabel>
              </Step>
            );
          })}
        </Stepper>
        <div className="stepper-container">
          {isLoading ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              minHeight="400px"
            >
              <CircularProgress />
            </Box>
          ) : (
            getStepContent(activeStep)
          )}
        </div>
        <Snackbar
          anchorOrigin={{
            vertical: "top",
            horizontal: "center",
          }}
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={handleCloseSnackbar}
          key={snackbar.message}
          style={{ zIndex: 9999 }}
        >
          <MuiAlert
            elevation={6}
            variant="filled"
            severity={snackbar.severity}
            onClose={handleCloseSnackbar}
            action={
              <IconButton
                size="small"
                color="inherit"
                onClick={handleCloseSnackbar}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            }
          >
            {snackbar.message}
          </MuiAlert>
        </Snackbar>
      </Paper>
    </div>
  );
};

export default CMOStepper;
